import { useCallback, useEffect, useRef, useState, type UIEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useAppSelector } from '@/store/hooks'
import { JobEntryCard } from './JobEntryCard'
import { LoadingMoreTechniciansIndicator } from './LoadingMoreTechniciansIndicator'
import { TechnicianQuickActions } from './TechnicianQuickActions'
import {
  clearAgendaDragPayload,
  getAgendaDragPayload,
  setAgendaDragPreview,
  setAgendaDragPayload,
  updateAgendaDragPreview,
} from './agenda-dnd'
import {
  agendaColumnReorderHeaderClass,
  agendaColumnReorderTargetClass,
  agendaDropTargetClass,
} from './agenda-drop-feedback'
import { useTechnicianColumnOrder } from './useTechnicianColumnOrder'
import {
  DAY_END_HOUR,
  DEFAULT_DAY_START_HOUR,
  HOUR_ROW_PX,
  computeColumns,
  detectConflicts,
  parseSlotRange,
  sameDay,
  toISODate,
  type TechnicianLane,
} from './grid-helpers'
import { PRELOAD_ROOT_PROPS } from './job-detail-preload'
import {
  API_DAY_END_MINUTES,
  computeDropTimeRange,
  getJobDurationMinutes,
  minutesToY,
  minutesToTimeString,
  resizePreviewMatchesTimeslot,
  snapMinutes,
  type ResizePreviewRange,
  yToMinutes,
} from './time-geometry'
import { JOB_STATUS, type Job } from '@/types'
import type { AgendaMutationHandlers } from '@/components/jobs/AgendaGrid'

interface DayViewProps extends Partial<AgendaMutationHandlers> {
  day: Date
  lanes: TechnicianLane[]
  jobsByTech: Map<string, Job[]>
  onEntryClick: (job: Job) => void
  enableTechnicianColumnReorder?: boolean
  enableDragDropAssignment?: boolean
  enableResizeDuration?: boolean
  onLoadMoreTechnicians?: () => void
  hasMoreTechnicians?: boolean
  isLoadingMoreTechnicians?: boolean
}

// Vertical inset applied to each timed block so back-to-back jobs (one ending
// exactly when the next begins) don't share an edge — a small top+bottom gap
// visually separates consecutive cards in a lane.
const BLOCK_GAP_PX = 3
const LOAD_MORE_TECHNICIANS_THRESHOLD_PX = 720

type ResizeState = {
  jobId: string
  startMinutes: number
  endMinutes: number
  originalEndMinutes: number
  laneGridTop: number
  cardElement: HTMLElement
  originalHeight: string
}

type ResizePreview = ResizePreviewRange & {
  jobId: string
}

type PendingResizeCommit = {
  jobId: string
  observedMutation: boolean
}

// Positions an event block in the hour grid, sized to its time range. Untimed
// jobs render as a single short block at the top of the lane.
function blockGeometry(job: Job, dayStartHour: number) {
  const range = parseSlotRange(job.start_time, job.end_time)
  const gridStartMin = dayStartHour * 60
  const gridEndMin = DAY_END_HOUR * 60
  if (!range) {
    return { top: 4, height: HOUR_ROW_PX - 8, untimed: true }
  }
  const start = Math.max(range.start, gridStartMin)
  const end = Math.min(range.end, gridEndMin)
  const top = ((start - gridStartMin) / 60) * HOUR_ROW_PX + BLOCK_GAP_PX
  const height = Math.max(
    28,
    ((end - start) / 60) * HOUR_ROW_PX - BLOCK_GAP_PX * 2
  )
  return { top, height, untimed: false }
}

function jobIntersectsMinute(job: Job, minute: number): boolean {
  const range = parseSlotRange(job.start_time, job.end_time)
  return Boolean(range && minute >= range.start && minute < range.end)
}

function assignedTechnicianIdsForDrag(job: Job, laneTechnicianId: string) {
  const assignedIds = (job.assigned_technicians ?? [])
    .map(technician => technician.id)
    .filter((id): id is string => typeof id === 'string' && id.length > 0)

  if (assignedIds.length === 0) return [laneTechnicianId]
  return Array.from(new Set(assignedIds))
}

function nextTechnicianIdsForDrop(
  payload: Extract<
    NonNullable<ReturnType<typeof getAgendaDragPayload>>,
    { type: 'scheduled-job' }
  >,
  targetTechnicianId: string
) {
  const currentIds =
    payload.technicianIds.length > 0
      ? payload.technicianIds
      : [payload.fromTechnicianId]

  if (payload.fromTechnicianId === targetTechnicianId) {
    return Array.from(new Set(currentIds))
  }

  const nextIds = currentIds.map(id =>
    id === payload.fromTechnicianId ? targetTechnicianId : id
  )
  if (!nextIds.includes(targetTechnicianId)) nextIds.push(targetTechnicianId)

  return Array.from(new Set(nextIds))
}

function dragStartY(
  payload: NonNullable<ReturnType<typeof getAgendaDragPayload>>,
  clientY: number
) {
  if (payload.type === 'technician-column') return clientY
  return clientY - payload.grabOffsetY
}

function dragPreviewLabel(start: number, end: number, technicianName: string) {
  return `${minutesToTimeString(start)} - ${minutesToTimeString(end)} · ${technicianName}`
}

// Day view (AC-2.3.1-013/-032/-044): hourly breakdown, one lane per technician.
// Left time-gutter + equal-width technician columns; blocks span their time
// range. Empty slots stay visible to surface capacity (AC-2.3.1-010/-030/-042).
export function DayView({
  day,
  lanes,
  jobsByTech,
  onEntryClick,
  onScheduleJob,
  onResizeJob,
  isMutatingJobId,
  enableTechnicianColumnReorder = true,
  enableDragDropAssignment = true,
  enableResizeDuration = true,
  onLoadMoreTechnicians,
  hasMoreTechnicians = false,
  isLoadingMoreTechnicians = false,
}: DayViewProps) {
  const { t } = useTranslation('jobs')
  const currentUserId = useAppSelector(state => state.auth.user?.id)
  const storageKey = `tigerflow.calendar.technicianOrder.${currentUserId ?? 'anonymous'}.${toISODate(day)}`
  const { orderedLanes, moveLane } = useTechnicianColumnOrder(storageKey, lanes)
  const dayStartHour = DEFAULT_DAY_START_HOUR
  const hourRows = Array.from(
    { length: DAY_END_HOUR - dayStartHour },
    (_, i) => dayStartHour + i
  )
  const hourLabels = [...hourRows, DAY_END_HOUR]
  const gridHeight = hourRows.length * HOUR_ROW_PX

  // Current-time marker (Figma "Chart marker" 850:12909): a horizontal now-line
  // with a time pill, drawn across the lanes only when viewing today and within
  // the visible grid hours. Ticks every 30s so the line + badge track the real
  // clock instead of freezing at first render.
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000)
    return () => clearInterval(id)
  }, [])
  const nowMin = now.getHours() * 60 + now.getMinutes()
  const showNow =
    toISODate(day) === toISODate(now) &&
    nowMin >= dayStartHour * 60 &&
    nowMin <= DAY_END_HOUR * 60
  const nowTop = ((nowMin - dayStartHour * 60) / 60) * HOUR_ROW_PX
  const nowLabel = `${String(now.getHours()).padStart(2, '0')}:${String(
    now.getMinutes()
  ).padStart(2, '0')}`
  const [dropLaneId, setDropLaneId] = useState<string | null>(null)
  const dropLaneIdRef = useRef<string | null>(null)
  const [dragOverTechnicianId, setDragOverTechnicianId] = useState<
    string | null
  >(null)
  const dragOverTechnicianIdRef = useRef<string | null>(null)
  const [draggingJobId, setDraggingJobId] = useState<string | null>(null)
  const [resizingJobId, setResizingJobId] = useState<string | null>(null)
  const [resizePreview, setResizePreview] = useState<ResizePreview | null>(null)
  const resizeStateRef = useRef<ResizeState | null>(null)
  const pendingResizeCommitRef = useRef<PendingResizeCommit | null>(null)
  const resizeFrameRef = useRef<number | null>(null)
  const canReorderTechnicianColumns = enableTechnicianColumnReorder
  const canDragDropAssignments =
    enableDragDropAssignment && Boolean(onScheduleJob)
  const canResizeDuration = enableResizeDuration && Boolean(onResizeJob)

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (
        !hasMoreTechnicians ||
        isLoadingMoreTechnicians ||
        !onLoadMoreTechnicians
      ) {
        return
      }

      const { scrollLeft, clientWidth, scrollWidth } = event.currentTarget
      if (
        scrollWidth - scrollLeft - clientWidth <=
        LOAD_MORE_TECHNICIANS_THRESHOLD_PX
      ) {
        onLoadMoreTechnicians()
      }
    },
    [hasMoreTechnicians, isLoadingMoreTechnicians, onLoadMoreTechnicians]
  )

  const setActiveDropLane = useCallback((nextLaneId: string | null) => {
    if (dropLaneIdRef.current === nextLaneId) return
    dropLaneIdRef.current = nextLaneId
    setDropLaneId(nextLaneId)
  }, [])

  const setActiveDragOverTechnician = useCallback(
    (nextTechnicianId: string | null) => {
      if (dragOverTechnicianIdRef.current === nextTechnicianId) return
      dragOverTechnicianIdRef.current = nextTechnicianId
      setDragOverTechnicianId(nextTechnicianId)
    },
    []
  )

  const clearResizeFrame = useCallback(() => {
    if (resizeFrameRef.current === null) return
    window.cancelAnimationFrame(resizeFrameRef.current)
    resizeFrameRef.current = null
  }, [])

  const updateResizeCardHeight = useCallback(
    (currentResize: ResizeState) => {
      const height = Math.max(
        28,
        minutesToY(currentResize.endMinutes, dayStartHour) -
          minutesToY(currentResize.startMinutes, dayStartHour) -
          BLOCK_GAP_PX * 2
      )

      clearResizeFrame()
      resizeFrameRef.current = window.requestAnimationFrame(() => {
        currentResize.cardElement.style.height = `${height}px`
        resizeFrameRef.current = null
      })
    },
    [clearResizeFrame, dayStartHour]
  )

  useEffect(() => {
    if (!resizePreview || resizingJobId) return

    const clearResizePreviewSoon = () => {
      const previewToClear = resizePreview
      queueMicrotask(() => {
        setResizePreview(current =>
          current === previewToClear ? null : current
        )
      })
    }

    let previewJob: Job | null = null
    for (const jobs of jobsByTech.values()) {
      previewJob = jobs.find(job => job.id === resizePreview.jobId) ?? null
      if (previewJob) break
    }

    if (!previewJob) {
      pendingResizeCommitRef.current = null
      clearResizePreviewSoon()
      return
    }

    if (
      resizePreviewMatchesTimeslot(
        resizePreview,
        previewJob.start_time,
        previewJob.end_time
      )
    ) {
      pendingResizeCommitRef.current = null
      clearResizePreviewSoon()
      return
    }

    const pendingResizeCommit = pendingResizeCommitRef.current
    if (
      !pendingResizeCommit ||
      pendingResizeCommit.jobId !== resizePreview.jobId
    ) {
      clearResizePreviewSoon()
      return
    }

    if (isMutatingJobId === resizePreview.jobId) {
      pendingResizeCommit.observedMutation = true
      return
    }

    if (pendingResizeCommit.observedMutation) {
      pendingResizeCommitRef.current = null
      clearResizePreviewSoon()
    }
  }, [isMutatingJobId, jobsByTech, resizePreview, resizingJobId])

  useEffect(() => {
    if (!resizingJobId) return

    const handlePointerMove = (event: PointerEvent) => {
      const currentResize = resizeStateRef.current
      if (!currentResize) return

      const nextEnd = Math.min(
        API_DAY_END_MINUTES,
        Math.max(
          Math.min(API_DAY_END_MINUTES, currentResize.startMinutes + 30),
          snapMinutes(
            yToMinutes(event.clientY, currentResize.laneGridTop, dayStartHour)
          )
        )
      )
      if (nextEnd === currentResize.endMinutes) return

      currentResize.endMinutes = nextEnd
      updateResizeCardHeight(currentResize)
      setResizePreview(current =>
        current?.jobId === currentResize.jobId && current.endMinutes === nextEnd
          ? current
          : {
              jobId: currentResize.jobId,
              startMinutes: currentResize.startMinutes,
              endMinutes: nextEnd,
            }
      )
    }

    const handlePointerUp = () => {
      const completedResize = resizeStateRef.current
      resizeStateRef.current = null
      if (
        completedResize &&
        completedResize.endMinutes !== completedResize.originalEndMinutes
      ) {
        const committedPreview = {
          jobId: completedResize.jobId,
          startMinutes: completedResize.startMinutes,
          endMinutes: completedResize.endMinutes,
        }
        pendingResizeCommitRef.current = {
          jobId: completedResize.jobId,
          observedMutation: false,
        }
        clearResizeFrame()
        setResizePreview(committedPreview)
        onResizeJob?.(completedResize.jobId, {
          start_time: minutesToTimeString(completedResize.startMinutes),
          end_time: minutesToTimeString(completedResize.endMinutes),
        })
      } else if (completedResize) {
        clearResizeFrame()
        completedResize.cardElement.style.height =
          completedResize.originalHeight
        setResizePreview(null)
      }
      setResizingJobId(null)
      if (!completedResize) setResizePreview(null)
    }

    const handlePointerCancel = () => {
      const cancelledResize = resizeStateRef.current
      resizeStateRef.current = null
      pendingResizeCommitRef.current = null
      clearResizeFrame()
      if (cancelledResize) {
        cancelledResize.cardElement.style.height =
          cancelledResize.originalHeight
      }
      setResizingJobId(null)
      setResizePreview(null)
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp, { once: true })
    window.addEventListener('pointercancel', handlePointerCancel, {
      once: true,
    })
    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
      window.removeEventListener('pointercancel', handlePointerCancel)
      clearResizeFrame()
    }
  }, [
    clearResizeFrame,
    dayStartHour,
    onResizeJob,
    resizingJobId,
    updateResizeCardHeight,
  ])

  return (
    <div
      className='h-[calc(100vh-250px)] min-h-[560px] overflow-auto bg-white'
      {...PRELOAD_ROOT_PROPS}
      onScroll={handleScroll}
    >
      <div className='flex min-w-max'>
        {/* Time gutter */}
        <div className='sticky left-0 z-30 w-16 shrink-0 bg-white'>
          <div className='h-16 border-b border-transparent bg-white' />
          <div
            className='relative border-r border-[#e7e9ef]'
            style={{ height: gridHeight }}
          >
            {hourLabels.map((h, i) => (
              <div
                key={h}
                className='absolute left-0 right-0 -translate-y-1/2 pr-3 text-right font-sans text-sm font-medium tabular-nums leading-none text-[#69758c]'
                style={{ top: i * HOUR_ROW_PX }}
              >
                {String(h).padStart(2, '0')}:00
              </div>
            ))}
          </div>
        </div>

        {/* Technician lanes */}
        <div className='relative flex flex-1'>
          {orderedLanes.map((lane, index) => {
            const dayJobs = (jobsByTech.get(lane.technicianId) ?? []).filter(
              j => Boolean(j.scheduled_date && sameDay(j.scheduled_date, day))
            )
            const conflicts = detectConflicts(dayJobs)
            const columns = computeColumns(dayJobs)
            const isDropActive = dropLaneId === lane.technicianId
            const isDropBlocked = isDropActive && Boolean(isMutatingJobId)
            const isColumnReorderActive =
              canReorderTechnicianColumns &&
              dragOverTechnicianId === lane.technicianId
            const showDropTint = isDropActive && !isDropBlocked
            return (
              <div
                key={lane.technicianId}
                className={cn(
                  'relative min-w-[240px] flex-1 border-r border-[#e7e9ef]',
                  agendaColumnReorderTargetClass(isColumnReorderActive)
                )}
              >
                {/* Lane header — tinted by the column colour (Figma 850:12822) */}
                <div
                  draggable={canReorderTechnicianColumns || undefined}
                  onDragStart={
                    canReorderTechnicianColumns
                      ? event => {
                          setAgendaDragPayload(event.dataTransfer, {
                            type: 'technician-column',
                            technicianId: lane.technicianId,
                            fromIndex: index,
                          })
                          event.dataTransfer.effectAllowed = 'move'
                        }
                      : undefined
                  }
                  onDragOver={
                    canReorderTechnicianColumns
                      ? event => {
                          const payload = getAgendaDragPayload(
                            event.dataTransfer
                          )
                          if (payload?.type !== 'technician-column') return
                          event.preventDefault()
                          event.dataTransfer.dropEffect = 'move'
                          setActiveDragOverTechnician(lane.technicianId)
                        }
                      : undefined
                  }
                  onDragLeave={
                    canReorderTechnicianColumns
                      ? event => {
                          if (
                            !event.currentTarget.contains(
                              event.relatedTarget as Node
                            )
                          ) {
                            if (
                              dragOverTechnicianIdRef.current ===
                              lane.technicianId
                            ) {
                              setActiveDragOverTechnician(null)
                            }
                          }
                        }
                      : undefined
                  }
                  onDrop={
                    canReorderTechnicianColumns
                      ? event => {
                          const payload = getAgendaDragPayload(
                            event.dataTransfer
                          )
                          if (payload?.type !== 'technician-column') return
                          event.preventDefault()
                          moveLane(payload.technicianId, lane.technicianId)
                          setActiveDragOverTechnician(null)
                          clearAgendaDragPayload()
                        }
                      : undefined
                  }
                  onDragEnd={
                    canReorderTechnicianColumns
                      ? () => {
                          setActiveDragOverTechnician(null)
                          clearAgendaDragPayload()
                        }
                      : undefined
                  }
                  className={cn(
                    'sticky top-0 z-20 flex h-16 flex-col justify-center gap-1 border-b border-[#e7e9ef] px-3',
                    canReorderTechnicianColumns &&
                      'cursor-grab active:cursor-grabbing',
                    agendaColumnReorderHeaderClass(isColumnReorderActive)
                  )}
                  style={{
                    boxShadow: `inset 0 -2px 0 ${lane.color}`,
                  }}
                >
                  <span className='flex w-full min-w-0 items-center gap-2'>
                    <span
                      className='h-3 w-3 shrink-0 rounded-full'
                      style={{ background: lane.color }}
                    />
                    <span className='min-w-0 flex-1 truncate font-sans text-base font-medium text-[#171a20]'>
                      {lane.name}
                    </span>
                    <TechnicianQuickActions lane={lane} />
                  </span>
                  <span className='w-full truncate font-sans text-xs font-normal leading-4 text-[#69758c]'>
                    {t('agenda.totalShort', { count: dayJobs.length })}
                  </span>
                </div>
                {/* Hour grid with positioned blocks */}
                <div
                  data-agenda-lane-grid='true'
                  onDragEnter={
                    canDragDropAssignments
                      ? event => {
                          const payload = getAgendaDragPayload(
                            event.dataTransfer
                          )
                          if (!payload || payload.type === 'technician-column')
                            return
                          setActiveDropLane(lane.technicianId)
                        }
                      : undefined
                  }
                  onDragOver={
                    canDragDropAssignments
                      ? event => {
                          const payload = getAgendaDragPayload(
                            event.dataTransfer
                          )
                          if (!payload || payload.type === 'technician-column')
                            return
                          event.preventDefault()
                          event.dataTransfer.dropEffect = isDropBlocked
                            ? 'none'
                            : 'move'
                          const rect =
                            event.currentTarget.getBoundingClientRect()
                          const { start, end } = computeDropTimeRange({
                            pointerMinutes: yToMinutes(
                              dragStartY(payload, event.clientY),
                              rect.top,
                              dayStartHour
                            ),
                            dayStartHour,
                            durationMinutes: payload.durationMinutes,
                          })
                          updateAgendaDragPreview(
                            event.clientX,
                            event.clientY,
                            isDropBlocked
                              ? null
                              : dragPreviewLabel(start, end, lane.name)
                          )
                          setActiveDropLane(lane.technicianId)
                        }
                      : undefined
                  }
                  onDragLeave={
                    canDragDropAssignments
                      ? event => {
                          if (
                            !event.currentTarget.contains(
                              event.relatedTarget as Node
                            )
                          ) {
                            if (dropLaneIdRef.current === lane.technicianId) {
                              setActiveDropLane(null)
                            }
                          }
                        }
                      : undefined
                  }
                  onDrop={
                    canDragDropAssignments
                      ? event => {
                          const payload = getAgendaDragPayload(
                            event.dataTransfer
                          )
                          if (!payload || payload.type === 'technician-column')
                            return
                          event.preventDefault()
                          setActiveDropLane(null)
                          setDraggingJobId(null)
                          if (isDropBlocked || !onScheduleJob) {
                            clearAgendaDragPayload()
                            return
                          }

                          const rect =
                            event.currentTarget.getBoundingClientRect()
                          const { start, end } = computeDropTimeRange({
                            pointerMinutes: yToMinutes(
                              dragStartY(payload, event.clientY),
                              rect.top,
                              dayStartHour
                            ),
                            dayStartHour,
                            durationMinutes: payload.durationMinutes,
                          })
                          onScheduleJob(payload.jobId, {
                            scheduled_date: toISODate(day),
                            start_time: minutesToTimeString(start),
                            end_time: minutesToTimeString(end),
                            technician_ids:
                              payload.type === 'scheduled-job'
                                ? nextTechnicianIdsForDrop(
                                    payload,
                                    lane.technicianId
                                  )
                                : [lane.technicianId],
                          })
                          clearAgendaDragPayload()
                        }
                      : undefined
                  }
                  className={cn(
                    'relative bg-white transition-colors',
                    agendaDropTargetClass(isDropActive, isDropBlocked)
                  )}
                  style={{ height: gridHeight }}
                >
                  {hourRows.map((h, i) => (
                    <div
                      key={h}
                      className='absolute left-0 right-0 border-b border-[#e7e9ef] odd:bg-white even:bg-[#fbfbfc]'
                      style={{ top: i * HOUR_ROW_PX, height: HOUR_ROW_PX }}
                    />
                  ))}
                  {showDropTint && (
                    <div
                      aria-hidden='true'
                      className='pointer-events-none absolute inset-0 bg-[#FFF1EA]/80'
                    />
                  )}
                  {showNow && (
                    <div
                      aria-hidden='true'
                      className='pointer-events-none absolute left-0 right-0 z-10'
                      style={{ top: nowTop }}
                    >
                      <div className='h-0.5 w-full bg-[#ff5a1f]' />
                      {index < orderedLanes.length - 1 ? (
                        <span className='absolute right-0 top-1/2 size-2 translate-x-1/2 -translate-y-1/2 rounded-full bg-[#ff5a1f]' />
                      ) : null}
                    </div>
                  )}
                  {dayJobs.map(job => {
                    const geo = blockGeometry(job, dayStartHour)
                    const conflict = conflicts.has(job.id)
                    const { col, totalCols } = columns.get(job.id) ?? {
                      col: 0,
                      totalCols: 1,
                    }
                    const widthPct = 100 / totalCols
                    const leftPct = col * widthPct
                    const canDragScheduledJob =
                      job.status === JOB_STATUS.planned ||
                      job.status === JOB_STATUS.assigned
                    const canResizeJob = canDragScheduledJob
                    const isMutating = job.id === isMutatingJobId
                    const activeResize =
                      resizePreview?.jobId === job.id ? resizePreview : null
                    const realtimeActive =
                      showNow && jobIntersectsMinute(job, nowMin)
                    const displayHeight = activeResize
                      ? Math.max(
                          28,
                          minutesToY(activeResize.endMinutes, dayStartHour) -
                            minutesToY(
                              activeResize.startMinutes,
                              dayStartHour
                            ) -
                            BLOCK_GAP_PX * 2
                        )
                      : geo.height
                    return (
                      <JobEntryCard
                        key={job.id}
                        job={job}
                        color={lane.color}
                        tint={lane.tint}
                        conflict={conflict}
                        realtimeActive={realtimeActive}
                        onClick={() => onEntryClick(job)}
                        draggable={
                          canDragDropAssignments &&
                          canDragScheduledJob &&
                          !isMutating
                        }
                        onDragStart={
                          canDragDropAssignments
                            ? event => {
                                setDraggingJobId(job.id)
                                setAgendaDragPayload(event.dataTransfer, {
                                  type: 'scheduled-job',
                                  jobId: job.id,
                                  fromTechnicianId: lane.technicianId,
                                  technicianIds: assignedTechnicianIdsForDrag(
                                    job,
                                    lane.technicianId
                                  ),
                                  durationMinutes: getJobDurationMinutes(job),
                                  status: job.status,
                                  grabOffsetY:
                                    event.clientY -
                                    event.currentTarget.getBoundingClientRect()
                                      .top,
                                  startTime: job.start_time,
                                  endTime: job.end_time,
                                })
                                setAgendaDragPreview(
                                  event.dataTransfer,
                                  event.currentTarget,
                                  event.clientX,
                                  event.clientY
                                )
                                event.dataTransfer.effectAllowed = 'move'
                              }
                            : undefined
                        }
                        onDragEnd={
                          canDragDropAssignments
                            ? () => {
                                setActiveDropLane(null)
                                setDraggingJobId(null)
                                clearAgendaDragPayload()
                              }
                            : undefined
                        }
                        ariaDisabled={!canDragScheduledJob}
                        isMutating={isMutating}
                        resizable={
                          canResizeDuration && canResizeJob && !isMutating
                        }
                        resizeEndLabel={
                          activeResize
                            ? minutesToTimeString(activeResize.endMinutes)
                            : null
                        }
                        onResizeStart={
                          canResizeDuration
                            ? event => {
                                event.preventDefault()
                                event.stopPropagation()
                                const range = parseSlotRange(
                                  job.start_time,
                                  job.end_time
                                )
                                if (!range || !onResizeJob) return
                                const laneGrid = event.currentTarget.closest(
                                  '[data-agenda-lane-grid="true"]'
                                )
                                if (!(laneGrid instanceof HTMLElement)) return
                                const cardElement = event.currentTarget.closest(
                                  '[data-agenda-job-card="true"]'
                                )
                                if (!(cardElement instanceof HTMLElement))
                                  return
                                const nextResize = {
                                  jobId: job.id,
                                  startMinutes: range.start,
                                  endMinutes: range.end,
                                  originalEndMinutes: range.end,
                                  laneGridTop:
                                    laneGrid.getBoundingClientRect().top,
                                  cardElement,
                                  originalHeight: cardElement.style.height,
                                }
                                pendingResizeCommitRef.current = null
                                resizeStateRef.current = nextResize
                                setResizingJobId(job.id)
                                setResizePreview({
                                  jobId: job.id,
                                  startMinutes: range.start,
                                  endMinutes: range.end,
                                })
                              }
                            : undefined
                        }
                        style={{
                          top: geo.top,
                          height: displayHeight,
                          left: `calc(${leftPct}% + 4px)`,
                          width: `calc(${widthPct}% - 8px)`,
                        }}
                        className={cn(
                          'absolute',
                          draggingJobId === job.id && 'opacity-0'
                        )}
                        compact={geo.height <= 96}
                      />
                    )
                  })}
                </div>
              </div>
            )
          })}
          {(hasMoreTechnicians || isLoadingMoreTechnicians) && (
            <LoadingMoreTechniciansIndicator
              loading={isLoadingMoreTechnicians}
              className='sticky top-0 h-16 border-b border-r border-[#e7e9ef] bg-white'
            />
          )}
          {showNow && (
            <div
              className='pointer-events-none absolute left-0 right-0 z-40'
              style={{ top: 64 + nowTop }}
            >
              <span className='absolute right-full -top-3 mr-1 rounded-xl bg-[#ff5a1f] px-3 py-1.5 font-sans text-xs font-medium tabular-nums leading-none text-white shadow-[0_8px_16px_-8px_rgba(255,90,31,0.8)]'>
                {nowLabel}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
